// convex/lib/orderTotals.ts
//
// Pure order-totals arithmetic, shared by the Convex server (convex/orders.ts)
// and the browser cart store (src/stores/cart-store.ts). Numbers in, numbers
// out — no Convex runtime imports, no I/O — so both runtimes can import it.
// All money values are integer cents unless noted.

/** Convert a menu price expressed in dollars to integer cents. */
export function menuPriceToCents(price: number): number {
  return Math.round(price * 100);
}

/** Clamp a discount to the range [0, subtotal] and round to whole cents. */
export function clampDiscountToSubtotal(discount: number, subtotal: number): number {
  return Math.round(Math.min(Math.max(0, discount), subtotal));
}

/** A single order line with all amounts already resolved to cents. */
export interface OrderLineInput {
  /** Base unit price in cents. */
  unitPrice: number;
  /** Sum of modifier price adjustments in cents (0 if none). */
  modifierTotal: number;
  /** Sum of customization prices in cents (0 if none). */
  customizationTotal?: number;
  quantity: number;
}

/** Line extended price: (unit + modifiers + customizations) * quantity. */
export function calcLineItemTotal(line: OrderLineInput): number {
  return (
    (line.unitPrice + line.modifierTotal + (line.customizationTotal ?? 0)) *
    line.quantity
  );
}

/** Sum of every line's extended price. */
export function calcSubtotal(lines: OrderLineInput[]): number {
  return lines.reduce((sum, line) => sum + calcLineItemTotal(line), 0);
}

/** Tax on the discounted subtotal, rounded to whole cents. */
export function calcTax(
  subtotal: number,
  appliedDiscount: number,
  taxRate: number,
): number {
  return Math.round((subtotal - appliedDiscount) * taxRate);
}

/** Grand total from already-resolved cents components.
 *
 * `serviceFee` is optional and defaults to 0 so existing callers (standard
 * pickup/delivery checkout) are unaffected; the poolside channel passes a
 * non-zero service fee resolved by convex/lib/poolFulfillment.ts. */
export function calcOrderTotal(args: {
  subtotal: number;
  appliedDiscount: number;
  tax: number;
  deliveryFee: number;
  tip: number;
  serviceFee?: number;
}): number {
  return (
    args.subtotal -
    args.appliedDiscount +
    args.tax +
    (args.serviceFee ?? 0) +
    args.deliveryFee +
    args.tip
  );
}
